import {
  selectGameType,
  selectPendingShootingActions,
  selectPhase,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectUnits,
  selectWarPhaseStage,
} from '../redux/reducers/sharedStateReducer/selectors';
import {GAME_TYPES} from '../constants/game';
import {PHASES, WAR_PHASE_STAGES} from '../constants/phases';
import {UNIT_TYPES} from '../configs/units';
import {SHOT_KINDS} from '../configs/shotAnimation';
import {
  BOT_COMBAT_ANIMATED_UP_TO_SPEED_MULTIPLIER,
  FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER,
} from '../configs/playbackSpeed';
import {VISIBLE_RANKED_BOT_ACTION_DELAY_MS} from '../configs/visibleRankedBots';
import {BOT_ACTION_INTERVAL_MS} from '../utils/bots/botConfig';
import {getBotActionIntervalMs} from '../utils/bots/botActionPace';
import {getReplayPlaybackSpeedMultiplier} from '../utils/replay/replayPlaybackSpeed';
import {checkIfShotMissed} from '../utils/gameState/shooting/checkIfShotMissed';
import {getPlayer0xColor} from "../utils/players/getPlayer0xColor";
import {NEUTRAL_PLAYER_ID} from "../constants/players";
import {MELEE_ANIMATION} from "../configs/meleeAnimation";

/**
 * Анимация выстрела: катапульта бросает валун, эльфы дают залп стрел (см.
 * HexGridRenderer.showShotAnimation). Зовется ПОСЛЕ того, как экшен выстрела
 * или переброса костей применен, — и для своего выстрела (клик по цели), и
 * для чужого (сеть, бот, запись матча).
 *
 * Выстрел ищется в состоянии по id записи: экшен могли отбросить (сюжетная
 * реплика, блокировка ввода, устаревшее действие) — тогда и показывать нечего.
 * У переброса запись с таким id в состоянии уже лежит, поэтому отброшенный
 * переброс узнается по hasRerolled: его выставляет только примененный экшен.
 *
 * ЗАПИСЬ МАТЧА (страница записи и витрина главного меню). До x5 залп идет
 * целиком и с обычной длительностью, как свой
 * выстрел: проигрыватель записи не исполняет следующее действие, пока залп
 * не отыграет (см. ReplayRunner), — шаг воспроизведения его не поджимает.
 * Быстрее x5 запись — уже перемотка: анимации нет. То же правило — у ударов
 * ближнего боя (см. playMeleeAnimation).
 *
 * Анимации нет, а итог выстрела виден сразу, как раньше:
 *  - в записи матча быстрее x5 и при ботах быстрее x2 — залпы ботов
 *    наезжали бы друг на друга;
 *  - когда чужой залп в живой партии не укладывается в шаг темпа даже
 *    сжатым (см. isPaced);
 *  - в скрытой вкладке — кадры там не идут, и залп отыграл бы только на
 *    возврате, когда этап давно закончился;
 *  - когда нет поля, отряда-стрелка или его ячейки.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params
 * @param {string} params.actionId — id записи в pendingShootingActions
 * @param {number} [params.delayMs] — пауза до пуска: переброс костей сначала
 *   озвучивает кости, и только потом выстрел
 * @param {boolean} [params.isReroll] — это переброс костей уже сделанного
 *   выстрела, а не новый выстрел
 * @param {boolean} [params.isPaced] — выстрел чужой (бот, соперник по сети,
 *   запись матча). В живой партии следующее действие придет через шаг
 *   темпа, и залп должен в него уложиться (см. resolveShotPaceMs); запись
 *   матча залп не поджимает. Свой выстрел идет в темпе самого игрока
 * @returns {boolean} пошла ли анимация
 */
export const playShotAnimation = (
  store, hexGridRenderer, {actionId, delayMs = 0, isReroll = false, isPaced = false} = {}
) => {
  if (!actionId || !hexGridRenderer || typeof hexGridRenderer.showShotAnimation !== 'function') {
    return false;
  }
  if (typeof document !== 'undefined' && document.hidden) {
    return false;
  }

  const state = store.getState();
  const isReplay = selectGameType(state) === GAME_TYPES.REPLAY;
  if (isReplay && getReplayPlaybackSpeedMultiplier() > FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER) {
    return false;
  }
  if (selectPhase(state) !== PHASES.WAR || selectWarPhaseStage(state) !== WAR_PHASE_STAGES.SHOOTING) {
    return false;
  }

  const action = (selectPendingShootingActions(state) || []).find((item) => item.id === actionId);
  if (!action) {
    return false;
  }
  if (isReroll && !action.hasRerolled) {
    return false;
  }
  const units = selectUnits(state) || [];
  const shooterUnit = units.find((unit) => unit.id === action.shooterUnitId);
  if (!shooterUnit) {
    return false;
  }

  // Запись матча залп не поджимает: проигрыватель ждет его конца сам
  const paceMs = isPaced && !isReplay ? resolveShotPaceMs(state, shooterUnit.ownerId) : undefined;
  // Боты быстрее x2 — анимации нет: залпы наезжали бы друг на друга
  if (paceMs && BOT_ACTION_INTERVAL_MS / paceMs > BOT_COMBAT_ANIMATED_UP_TO_SPEED_MULTIPLIER) {
    return false;
  }

  try {
    return hexGridRenderer.showShotAnimation(action, {
      kind: resolveShotKind(shooterUnit),
      units,
      delayMs,
      paceMs,
      round: selectRound(state),
      isMiss: checkIfShotMissed(action, selectSpecialBuildings(state)),
      color: resolveShooterColor(shooterUnit, selectPlayers(state)),
    });
  } catch (error) {
    // Анимация — украшение: ее поломка не должна ронять разбор экшена
    console.warn('[Shot] не удалось показать выстрел', error);
    return false;
  }
};

/** Цвет света залпа — цвет игрока-стрелка; у нейтрального свой: его черный не светится */
const resolveShooterColor = (shooterUnit, players) => {
  const owner = (players || []).find((player) => player.id === shooterUnit.ownerId);
  return owner && shooterUnit.ownerId !== NEUTRAL_PLAYER_ID ? getPlayer0xColor(owner.color) : MELEE_ANIMATION.neutralColor;
};

/**
 * Во сколько раз сжат по времени идущий залп этого выстрела (1 — идет с
 * обычной длительностью или залпа нет). По этому множителю идет и звук
 * выстрела: у сжатого залпа удар должен прозвучать в момент попадания, а не
 * после него (см. SoundManager.playShooting).
 */
export const getShotTimeFactor = (hexGridRenderer, actionId) => {
  const timeFactor = hexGridRenderer?.getShotTimeFactor?.(actionId);
  return Number.isFinite(timeFactor) && timeFactor > 0 && timeFactor <= 1 ? timeFactor : 1;
};

/**
 * Шаг темпа чужих действий в живой партии: через столько придет следующее.
 * Запись матча темпа не задает: ее проигрыватель сам ждет конца анимации
 * (см. ReplayRunner).
 *  - Боты локальной партии — шагом, выбранным перед партией (у обучения свой).
 *  - Сетевая партия: «явный» бот ходит ровно и быстрее прочих
 *    (VISIBLE_RANKED_BOT_ACTION_DELAY_MS); пометка у него открытая, для всех
 *    игроков. Остальные — и скрытые боты, и живые соперники — не чаще
 *    стандартного шага ботов: его и берем, точного темпа у них нет.
 *
 * @param {Object} state
 * @param {string} [shooterOwnerId] — владелец стреляющего отряда
 * @returns {number}
 */
export const resolveShotPaceMs = (state, shooterOwnerId) => {
  if (selectGameType(state) === GAME_TYPES.LOCAL) {
    return getBotActionIntervalMs();
  }

  const shooterOwner = (selectPlayers(state) || []).find((player) => player.id === shooterOwnerId);
  return shooterOwner?.isVisibleBot ? VISIBLE_RANKED_BOT_ACTION_DELAY_MS : BOT_ACTION_INTERVAL_MS;
};

/**
 * Чем стреляет отряд. Катапульта (любой фракции, включая эльфийскую) бросает
 * валун; остальные стрелки — боевые эльфы — пускают стрелы. По картинке отряда
 * этого не узнать: эльф без образа рисуется общим силуэтом
 */
export const resolveShotKind = (shooterUnit) => (
  shooterUnit?.type === UNIT_TYPES.D4.id ? SHOT_KINDS.BOULDER : SHOT_KINDS.ARROWS
);
